import React, { useCallback, useContext, useEffect, useMemo, useState } from "react";
import Swal from "sweetalert2";
import { AuthContext } from "../contexts/auth";
import { PencilSquareIcon, TrashIcon, ClockIcon } from "@heroicons/react/24/outline";

import { deleteTimeSlotCompany, deleteWeeklyWorkingHoursSlot, updateWeeklyWorkingHoursCompany, deleteWeeklyWorkingHours, getWeeklyWorkingHoursCompoundById } from '../services/api_working_hours';

import WeekDayEditModal from "./wwh_day_edit_modal";
import Fetching from "./fetching";
import Empty from "./empty";
import CustomTooltipComponent from "./custom_tooltip_component";
import WeekCalendarEditShift from "./week_calendar_edit_shift";

const getOuLabel = (wh) => wh?.ou_name || wh?.ouName || wh?.ou?.name || "";

export default function ShiftTableWeeklyShow({
  weeklyWorkingHours = [],
  whCount,
  loading = false,
  selectedUnit = null,
  allowHiddenBreaks = false,

  onUpdateWeekWorkingHours,
  onDeleteWeekShift,
  onDeleteWeekDay,
  onDeleteWeekTimeSlot,
}) {
  const { token } = useContext(AuthContext);

  const [weekWorkingHoursData, setWeekWorkingHoursData] = useState(
    Array.isArray(weeklyWorkingHours) ? weeklyWorkingHours : []
  );

  const [editingShiftId, setEditingShiftId] = useState(null);

  // edit nome
  const [isEditingName, setIsEditingName] = useState({});
  const [newNameWwh, setNewNameWwh] = useState("");
  const [isSavingName, setIsSavingName] = useState(false);

  // modale edit giorno
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [activeWeekDay, setActiveWeekDay] = useState(null); // 1..7
  const [activeWwh, setActiveWwh] = useState(null);
  const [activeTimeSlotId, setActiveTimeSlotId] = useState(null);
  const [defaultMode, setDefaultMode] = useState(null);

  // sync prop -> state
  useEffect(() => {
    setWeekWorkingHoursData(Array.isArray(weeklyWorkingHours) ? weeklyWorkingHours : []);
    setIsEditingName({});
    setNewNameWwh("");
  }, [weeklyWorkingHours]);

  const sorted = useMemo(() => {
    return [...(weekWorkingHoursData || [])].sort((a, b) =>
      String(a?.name || "").localeCompare(String(b?.name || ""))
    );
  }, [weekWorkingHoursData]);

  const getOuId = (wwh) =>
    wwh?.ou?.id ??
    wwh?.ou_id ??
    (typeof wwh?.ou === "number" ? wwh.ou : null) ??
    null;
  // ----------------------------------------------------
  // Modal open/close
  // ----------------------------------------------------
  const openDayModal = useCallback(({ wwhObj, dayIndex0, timeSlotId = null, mode = null }) => {
    if (!wwhObj || dayIndex0 == null) return;

    setActiveWwh(wwhObj);
    setActiveWeekDay(dayIndex0 + 1);
    setActiveTimeSlotId(timeSlotId);
    setDefaultMode(mode); // "working_hour" | "custom" | null
    setDayModalOpen(true);
  }, []);

  const closeDayModal = useCallback(() => {
    setDayModalOpen(false);
    setActiveWeekDay(null);
    setActiveWwh(null);
    setActiveTimeSlotId(null);
  }, []);

  // ----------------------------------------------------
  // Refetch singolo turno modificato
  // ----------------------------------------------------
  const refetchActiveWeeklyById = useCallback(async () => {
    if (!token) return;
    if (!activeWwh?.id) return;

    const updated = await getWeeklyWorkingHoursCompoundById(token, activeWwh.id);

    // aggiorna SOLO l’elemento modificato nella lista già a schermo
    setWeekWorkingHoursData((prev) =>
      (prev || []).map((x) => (x.id === updated.id ? updated : x))
    );

    // riallinea anche l’oggetto in modale
    setActiveWwh(updated);
  }, [token, activeWwh?.id]);

  // ----------------------------------------------------
  // Delete weekly shift (whole)
  // ----------------------------------------------------
  const handleDeleteWeekShift = useCallback(
    async (id) => {
      const res = await Swal.fire({
        icon: "warning",
        text:
          "Il turno settimanale verrà eliminato anche dal calendario dei dipendenti ai quali è stato assegnato. Sei sicuro di voler procedere?",
        showCancelButton: true,
        confirmButtonText: "Sì",
        cancelButtonText: "Annulla",
        cancelButtonColor: "#d1d5db",
      });
      if (!res.isConfirmed) return;

      try {
        await deleteWeeklyWorkingHours(token, id);

        setWeekWorkingHoursData((prev) => (prev || []).filter((x) => x.id !== id));
        onDeleteWeekShift?.(id);

        Swal.fire({ position: "center", icon: "success", title: "Eliminato!", showConfirmButton: false, timer: 1200 });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Oops...", text: "Errore durante l'eliminazione del turno" });
      }
    },
    [token, onDeleteWeekShift]
  );

  // ----------------------------------------------------
  // Delete weekly-working-hours-slot (slotByWeekday)
  // ----------------------------------------------------
  const handleDeleteWeeklyWorkingHourSlot = useCallback(
    async (slotByWeekdayId) => {
      try {
        await deleteWeeklyWorkingHoursSlot(token, slotByWeekdayId);

        // update locale (veloce) + callback
        setWeekWorkingHoursData((prev) =>
          (prev || []).map((wwh) => ({
            ...wwh,
            slots_by_weekday: (wwh.slots_by_weekday || []).filter((x) => x.id !== slotByWeekdayId),
          }))
        );
        onDeleteWeekDay?.(slotByWeekdayId);

        Swal.fire({ position: "center", icon: "success", title: "Eliminato!", showConfirmButton: false, timer: 1200 });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Oops...", text: "Errore durante l'eliminazione del giorno" });
      }
    },
    [token, onDeleteWeekDay]
  );

  // ----------------------------------------------------
  // Delete single time-slot (custom only)
  // ----------------------------------------------------
  const handleDeleteWeekTimeSlot = useCallback(
    async (timeSlotId, slotByWeekdayObj, wwhId) => {
      try {
        const isLastTimeSlot = (slotByWeekdayObj?.time_slots || []).length === 1;

        if (isLastTimeSlot) {
          await handleDeleteWeeklyWorkingHourSlot(slotByWeekdayObj.id);
          return;
        }

        await deleteTimeSlotCompany(token, timeSlotId);

        setWeekWorkingHoursData((prev) =>
          (prev || []).map((wwh) => {
            if (wwh.id !== wwhId) return wwh;

            return {
              ...wwh,
              slots_by_weekday: (wwh.slots_by_weekday || []).map((sbw) => {
                if (sbw.id !== slotByWeekdayObj.id) return sbw;
                return { ...sbw, time_slots: (sbw.time_slots || []).filter((ts) => ts.id !== timeSlotId) };
              }),
            };
          })
        );

        onDeleteWeekTimeSlot?.(timeSlotId);

        Swal.fire({ position: "center", icon: "success", title: "Eliminato!", showConfirmButton: false, timer: 1200 });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Oops...", text: "Errore durante l'eliminazione della fascia oraria" });
      }
    },
    [token, onDeleteWeekTimeSlot, handleDeleteWeeklyWorkingHourSlot]
  );

  // ----------------------------------------------------
  // Edit name
  // ----------------------------------------------------
  const handleEditNameClick = useCallback((idx, name) => {
    setNewNameWwh(name || "");
    setIsEditingName((p) => ({ ...p, [idx]: true }));
  }, []);

  const handleCancelNameClick = useCallback((idx) => {
    setIsEditingName((p) => ({ ...p, [idx]: false }));
    setNewNameWwh("");
  }, []);

  const handleSaveNameClick = useCallback(
    async (idx) => {
      const wwh = sorted[idx];
      if (!wwh?.id) return;

      try {
        setIsSavingName(true);
        const updated = await updateWeeklyWorkingHoursCompany(token, wwh.id, newNameWwh);

        setWeekWorkingHoursData((prev) =>
          (prev || []).map((x) => (x.id === wwh.id ? { ...x, name: updated?.name ?? newNameWwh } : x))
        );

        onUpdateWeekWorkingHours?.(wwh.id, updated?.name ?? newNameWwh);
        setIsEditingName((p) => ({ ...p, [idx]: false }));

        Swal.fire({ position: "center", icon: "success", title: "Salvato!", showConfirmButton: false, timer: 1200 });
      } catch (error) {
        console.error(error);
        Swal.fire({ icon: "error", title: "Oops...", text: "Errore durante l'aggiornamento" });
      } finally {
        setIsSavingName(false);
      }
    },
    [sorted, token, newNameWwh, onUpdateWeekWorkingHours]
  );

  // ----------------------------------------------------
  // UI states
  // ----------------------------------------------------
  if (loading) {
    return (
      <div className="min-w-full text-center text-sm font-medium text-gray-600 py-5">
        <Fetching />
      </div>
    );
  }

  if (!sorted.length) {
    return (
      <div className="text-center text-sm text-gray-600 font-medium py-10">
        <Empty />
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* header */}
      <div
        className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 px-3 pb-4 sticky bg-white md:top-[237px] lg:top[230px]"
        style={{ zIndex: 20 }}
      >
        <div className="min-w-0">
          <div className="text-indigo-700 font-semibold text-sm uppercase">Turni settimanali</div>
          <div className="text-xs text-gray-500 mt-0.5">
            {selectedUnit?.name ? `Unità selezionata: ${selectedUnit.name}` : "Turni generici (non collegati ad alcuna unità)"}
          </div>
        </div>

        <div className="inline-flex items-center gap-2 text-xs font-semibold text-gray-600">
          <ClockIcon className="h-4 w-4" />
          {whCount ?? sorted.length} turn{(whCount ?? sorted.length) === 1 ? "o" : "i"}
        </div>
      </div>

      {/* cards */}
      <div className="space-y-3">
        {sorted.map((wwh, idx) => {
          const slotsByDay = Array(8).fill(null); // index 0 unused
          (wwh.slots_by_weekday || []).forEach((slot) => {
            const wd = slot.week_day;
            if (wd >= 1 && wd <= 7) slotsByDay[wd] = slot;
          });

          const isEditingThis = editingShiftId === wwh.id;

          return (
            <div key={wwh.id || idx} className="rounded-2xl border border-gray-200 bg-white shadow-sm overflow-hidden">
              {/* card header */}
              <div className="px-4 py-3 border-b border-gray-100 flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 bg-gray-100">
                <div className="min-w-0">
                  {!isEditingName[idx] ? (
                    <div className="flex items-center gap-2">
                      <div className="text-sm font-semibold text-gray-900 truncate capitalize">{wwh.name}</div>

                      {isEditingThis && (
                        <CustomTooltipComponent title="Modifica nome" placement="right" arrow>
                          <button
                            type="button"
                            onClick={() => handleEditNameClick(idx, wwh.name)}
                            className="rounded-md p-1 text-indigo-700 hover:bg-indigo-50"
                          >
                            <PencilSquareIcon className="h-5 w-5" />
                          </button>
                        </CustomTooltipComponent>
                      )}
                    </div>
                  ) : (
                    <div className="flex flex-col sm:flex-row sm:items-center gap-2">
                      <input
                        value={newNameWwh}
                        onChange={(e) => setNewNameWwh(e.target.value)}
                        className="rounded-lg border border-gray-200 px-3 py-2 text-sm w-full sm:w-80 focus:outline-none focus:ring-2 focus:ring-indigo-200"
                      />
                      <div className="flex gap-2">
                        <button
                          type="button"
                          disabled={isSavingName}
                          onClick={() => handleSaveNameClick(idx)}
                          className="rounded-lg bg-indigo-600 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-500 disabled:opacity-60"
                        >
                          {isSavingName ? "Salvo..." : "Salva"}
                        </button>
                        <button
                          type="button"
                          onClick={() => handleCancelNameClick(idx)}
                          className="rounded-lg bg-gray-300 px-3 py-2 text-xs font-semibold text-gray-700 hover:bg-gray-200"
                        >
                          Annulla
                        </button>
                      </div>
                    </div>
                  )}

                  <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-gray-500">
                    {!!wwh?.ou && (
                      <span className="rounded-full bg-indigo-50 px-2 py-1 font-semibold text-indigo-700 border border-indigo-100 truncate w-auto max-w-24">
                        {getOuLabel(wwh) || ""}
                      </span>
                    )}
                  </div>
                </div>

                <div className="flex gap-2 justify-end">
                  <button
                    type="button"
                    onClick={() => setEditingShiftId(isEditingThis ? null : wwh.id)}
                    className={`inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold border uppercase focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500 ${
                      isEditingThis
                        ? "bg-gray-300 text-gray-700 border-gray-300 hover:bg-gray-200"
                        : "bg-indigo-600 text-white border-indigo-600 hover:bg-indigo-500"
                    }`}
                  >
                    <PencilSquareIcon className="h-4 w-4" />
                    {isEditingThis ? "Chiudi" : "Modifica"}
                  </button>

                  <button
                    type="button"
                    onClick={() => handleDeleteWeekShift(wwh.id)}
                    className="inline-flex items-center gap-2 rounded-lg px-3 py-2 text-xs font-semibold bg-white text-red-600 border border-gray-200 hover:bg-red-50 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-500"
                  >
                    <TrashIcon className="h-4 w-4" />
                    ELIMINA
                  </button>
                </div>
              </div>

              {/* calendar */}
              <div className="p-4">
                <WeekCalendarEditShift
                  slotsByDay={slotsByDay}
                  weekWorkingHourId={wwh.id}
                  editingShiftId={editingShiftId}
                  handleAddTimeSlot={(dayIndex0) => {
                    const sbw = slotsByDay[dayIndex0 + 1] || null;

                    const hasManualSlots = Array.isArray(sbw?.time_slots) && sbw.time_slots.length > 0;

                    // se ci sono fasce manuali => apro custom, altrimenti apro associa turno giornaliero
                    const mode = hasManualSlots ? "custom" : "working_hour";

                    openDayModal({ wwhObj: wwh, dayIndex0, timeSlotId: null, mode });
                  }}
                  onEditDay={(dayIndex0, timeSlotId) => {
                    // matita: sempre custom + editor sullo slot
                    openDayModal({ wwhObj: wwh, dayIndex0, timeSlotId, mode: "custom" });
                  }}
                  onDeleteWeeklyWorkingHourSlot={handleDeleteWeeklyWorkingHourSlot}
                  onDeleteTimeSlot={(timeSlotId, slotByWeekdayObj) =>
                    handleDeleteWeekTimeSlot(timeSlotId, slotByWeekdayObj, wwh.id)
                  }
                />
              </div>
            </div>
          );
        })}
      </div>
      
      <WeekDayEditModal
        token={token}
        open={dayModalOpen}
        weeklyWorkingHour={activeWwh}
        weekDay={activeWeekDay}
        selectedOuId={getOuId(activeWwh)} 
        allowHiddenBreaks={allowHiddenBreaks}
        initialTimeSlotId={activeTimeSlotId}
        defaultMode={defaultMode}
        onClose={closeDayModal}
        onSaved={refetchActiveWeeklyById}
      />
    </div>
  );
}